/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useCallback, useRef } from 'react';

import { getResizeSizeFromPointer, isVerticalDock } from './drawerLayout';
import { getResizeHandleSx } from './CommandIntelligenceDrawerResizeHandle.styles';
import { Box, useTheme } from '@mui/material';

const CommandIntelligenceDrawerResizeHandle = ({
  enabled,
  anchor = 'right',
  sizePx,
  onChangeSize,
  clampSize,
  onCommit,
  ariaLabel,
}) => {
  const theme = useTheme();
  const boxRef = useRef(null);
  const latestSizeRef = useRef(sizePx);
  const startRef = useRef({ x: 0, y: 0, size: 0 });

  const handlePointerDown = useCallback(
    event => {
      if (!enabled || event?.button !== 0) {
        return;
      }

      event?.preventDefault?.();
      const clientX = event?.clientX ?? 0;
      const clientY = event?.clientY ?? 0;
      const pointerId = event?.pointerId;

      startRef.current = { x: clientX, y: clientY, size: sizePx };
      latestSizeRef.current = sizePx;
      const el = boxRef?.current;

      el?.setPointerCapture?.(pointerId);
    },
    [enabled, sizePx]
  );

  const handlePointerMove = useCallback(
    event => {
      const el = boxRef?.current;
      const pointerId = event?.pointerId;

      if (!el?.hasPointerCapture?.(pointerId)) {
        return;
      }

      const start = startRef?.current ?? {};
      const nextRaw = getResizeSizeFromPointer(anchor, start, event);
      const next = clampSize(nextRaw);

      latestSizeRef.current = next;

      onChangeSize(next);
    },
    [anchor, clampSize, onChangeSize]
  );

  const releaseAndCommit = useCallback(
    event => {
      const el = boxRef?.current;
      const pointerId = event?.pointerId;

      if (!el?.hasPointerCapture?.(pointerId)) {
        return;
      }

      el?.releasePointerCapture?.(pointerId);

      onCommit?.(latestSizeRef?.current);
    },
    [onCommit]
  );

  if (!enabled) {
    return null;
  }

  const orientation = isVerticalDock(anchor) ? 'vertical' : 'horizontal';

  return (
    <Box
      ref={boxRef}
      role="separator"
      aria-orientation={orientation}
      aria-label={ariaLabel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={releaseAndCommit}
      onPointerCancel={releaseAndCommit}
      sx={getResizeHandleSx(theme, { anchor })}
    />
  );
};

export default CommandIntelligenceDrawerResizeHandle;
